import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  blockTenantSetupStep,
  completeTenantSetupSession,
  completeTenantSetupStep,
  createTenantSetupSession,
  getTenantSetupContext,
  getTenantSetupEvents,
  reopenTenantSetupSession,
  skipTenantSetupStep,
  startTenantSetupSession,
} from '../api/setupWizardApi';
import { useAuth } from '../auth/AuthContext';
import { hasPermission } from '../auth/permissions';
import { DataTable } from './DataTable';
import { LoadingSkeleton } from './LoadingSkeleton';
import { StatusBadge } from './StatusBadge';
import { TenantSetupRequirementsPanel } from './TenantSetupRequirementsPanel';
import {
  formatSetupStepTitle,
  formatSetupWarning,
  safeTranslate,
} from '../utils/systemLabels';
import {
  evidenceStatusLabelKey,
  formatEvidenceObservedAt,
  wizardStepEvidence,
} from '../utils/setupEvidence';

function StepTitle({ stepCode, t }) {
  return formatSetupStepTitle(stepCode, t);
}

function EvidenceStatusText({ status, t }) {
  return t(evidenceStatusLabelKey(status), { defaultValue: status || t('common:empty_value') });
}

export function TenantSetupWizardTab({ tenantId }) {
  const { t } = useTranslation(['setupWizard', 'common', 'statuses']);
  const { session: authSession } = useAuth();
  const canManage = hasPermission(authSession, 'system.setup_wizard.manage');
  const canComplete = hasPermission(authSession, 'system.setup_wizard.complete');

  const [context, setContext] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState(null);
  const [dialogReason, setDialogReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [contextResponse, eventsResponse] = await Promise.all([
        getTenantSetupContext(tenantId),
        getTenantSetupEvents(tenantId, { per_page: 50 }).catch(() => ({ data: [] })),
      ]);
      setContext(contextResponse.data || contextResponse);
      setEvents(eventsResponse.data || []);
    } catch {
      setError(t('setupWizard:tenant_tab.load_failed'));
    } finally {
      setLoading(false);
    }
  }, [tenantId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const runAction = useCallback(async (action) => {
    setMessage('');
    setError('');

    try {
      await action();
      setMessage(t('setupWizard:tenant_tab.action_success'));
      setDialog(null);
      setDialogReason('');
      load();
    } catch (err) {
      const errors = err?.response?.data?.errors || {};
      const messageKey = Array.isArray(errors.message_key) ? errors.message_key[0] : null;
      let apiMessage = err?.response?.data?.message;
      if (typeof messageKey === 'string' && messageKey.startsWith('setupWizard.')) {
        const localKey = messageKey.slice('setupWizard.'.length);
        const translated = t(`setupWizard:${localKey}`, { defaultValue: '' });
        if (translated) {
          apiMessage = translated;
        }
      }
      setError(apiMessage || t('setupWizard:tenant_tab.action_failed'));
    }
  }, [load, t]);

  const handleCompleteStep = useCallback((stepCode) => {
    runAction(() => completeTenantSetupStep(tenantId, stepCode, {}));
  }, [runAction, tenantId]);

  const handleCreateSession = () => runAction(() => createTenantSetupSession(tenantId));
  const handleStartSession = () => runAction(() => startTenantSetupSession(tenantId));
  const handleCompleteSession = () => runAction(() => completeTenantSetupSession(tenantId, {}));
  const handleReopenSession = () => runAction(() => reopenTenantSetupSession(tenantId, {}));

  const handleSkipStep = () => {
    if (!dialog?.stepCode) return;
    runAction(() => skipTenantSetupStep(tenantId, dialog.stepCode, { reason: dialogReason || null }));
  };

  const handleBlockStep = () => {
    if (!dialog?.stepCode) return;
    runAction(() => blockTenantSetupStep(tenantId, dialog.stepCode, { reason: dialogReason }));
  };

  const evidenceGateForColumns = context?.evidence_gate || null;

  const stepColumns = useMemo(() => [
    {
      id: 'step_code',
      label: t('setupWizard:tenant_tab.columns.step'),
      render: (row) => <StepTitle stepCode={row.step_code} t={t} />,
    },
    {
      id: 'status',
      label: t('setupWizard:tenant_tab.columns.manual_status'),
      render: (row) => <StatusBadge status={row.status} category="setup_step" />,
    },
    {
      id: 'app_evidence',
      label: t('setupWizard:tenant_tab.columns.app_evidence'),
      render: (row) => {
        if (row.step_code === 'review_launch') {
          return t('setupWizard:tenant_tab.evidence_human_review');
        }
        const ev = wizardStepEvidence(evidenceGateForColumns, row.step_code);
        if (!ev) {
          return t('setupWizard:tenant_tab.evidence_not_mapped');
        }
        const observed = formatEvidenceObservedAt(ev, evidenceGateForColumns?.reported_at);
        const source = ev.source || evidenceGateForColumns?.source || 'mafert_app_setup_center';
        return (
          <Stack spacing={0.25}>
            <Typography variant="body2">
              <EvidenceStatusText status={ev.status} t={t} />
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('setupWizard:tenant_tab.evidence_source')}: {source}
            </Typography>
            {observed && (
              <Typography variant="caption" color="text.secondary">
                {t('setupWizard:tenant_tab.evidence_observed_at')}: {observed}
              </Typography>
            )}
            {(ev.app_steps || []).length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {(ev.app_steps || []).map((s) => `${s.code}=${s.status}`).join(', ')}
              </Typography>
            )}
          </Stack>
        );
      },
    },
    {
      id: 'required',
      label: t('setupWizard:tenant_tab.columns.required'),
      render: (row) => (row.required ? t('statuses:yes') : t('statuses:no')),
    },
    { id: 'completed_at', label: t('setupWizard:tenant_tab.columns.completed_at') },
    {
      id: 'actions',
      label: t('common:actions'),
      render: (row) => {
        if (!canManage && !canComplete) {
          return t('common:empty_value');
        }

        if (row.status === 'completed' || row.status === 'skipped') {
          return t('common:empty_value');
        }

        return (
          <Stack direction="row" spacing={1}>
            {(canManage || canComplete) && (
              <Button size="small" variant="outlined" onClick={() => handleCompleteStep(row.step_code)}>
                {t('setupWizard:actions.complete_step')}
              </Button>
            )}
            {canManage && (
              <>
                <Button size="small" onClick={() => setDialog({ type: 'skip', stepCode: row.step_code })}>
                  {t('setupWizard:actions.skip_step')}
                </Button>
                <Button size="small" color="warning" onClick={() => setDialog({ type: 'block', stepCode: row.step_code })}>
                  {t('setupWizard:actions.block_step')}
                </Button>
              </>
            )}
          </Stack>
        );
      },
    },
  ], [t, canManage, canComplete, handleCompleteStep, evidenceGateForColumns]);

  const eventColumns = useMemo(() => [
    {
      id: 'event_type',
      label: t('setupWizard:events.columns.event'),
      render: (row) => safeTranslate(t, `setupWizard:event_types.${row.event_type}`, row.event_type),
    },
    {
      id: 'step_code',
      label: t('setupWizard:events.columns.step'),
      render: (row) => (row.step_code ? formatSetupStepTitle(row.step_code, t) : t('common:empty_value')),
    },
    {
      id: 'message',
      label: t('common:message'),
      render: (row) => formatSetupWarning(row.message) || row.message || t('common:empty_value'),
    },
    { id: 'created_at', label: t('setupWizard:events.columns.at') },
  ], [t]);

  if (loading) {
    return <LoadingSkeleton type="Detail" />;
  }

  if (error && !context) {
    return <Alert severity="error">{error}</Alert>;
  }

  const setupSession = context?.setup_session;
  const warnings = context?.warnings || [];
  const evidenceGate = context?.evidence_gate || null;
  const evidenceBlocksComplete = Boolean(
    evidenceGate?.gate_enabled && evidenceGate?.can_complete === false
  );

  return (
    <Stack spacing={2}>
      <TenantSetupRequirementsPanel tenantId={tenantId} />
      {message && <Alert severity="success">{message}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}

      {!setupSession && (
        <Alert severity="info">
          {t('setupWizard:tenant_tab.no_session')}
          {canManage && (
            <Box sx={{ mt: 1 }}>
              <Button variant="contained" onClick={handleCreateSession}>
                {t('setupWizard:actions.create_session')}
              </Button>
            </Box>
          )}
        </Alert>
      )}

      {setupSession && (
        <>
          <Stack spacing={1}>
            <Typography variant="subtitle1">{t('setupWizard:tenant_tab.summary')}</Typography>
            <Typography variant="body2">
              {t('common:status')}: <StatusBadge status={context.setup_status} category="setup_session" />
            </Typography>
            <Typography variant="body2">
              {t('setupWizard:tenant_tab.progress')}: {Math.round(context.progress_percent ?? 0)}%
            </Typography>
            <Typography variant="body2">
              {t('setupWizard:tenant_tab.current_step')}: {context.current_step_code
                ? <StepTitle stepCode={context.current_step_code} t={t} />
                : t('common:empty_value')}
            </Typography>
            <Typography variant="body2">
              {t('setupWizard:tenant_tab.can_launch')}: {context.can_launch ? t('statuses:yes') : t('statuses:no')}
            </Typography>
            {(context.required_missing_steps || []).length > 0 && (
              <Typography variant="body2" color="warning.main">
                {t('setupWizard:tenant_tab.missing_steps')}:{' '}
                {(context.required_missing_steps || []).map((code) => formatSetupStepTitle(code, t)).join(', ')}
              </Typography>
            )}
          </Stack>

          {evidenceGate && (
            <Alert
              severity={
                evidenceGate.evidence_supports_completion
                  ? 'success'
                  : (evidenceGate.gate_enabled ? 'warning' : 'info')
              }
              data-testid="setup-evidence-gate"
            >
              <Typography variant="subtitle2">{t('setupWizard:tenant_tab.evidence_title')}</Typography>
              <Typography variant="body2">
                {evidenceGate.evidence_supports_completion
                  ? t('setupWizard:tenant_tab.evidence_ready')
                  : t('setupWizard:tenant_tab.evidence_blocked')}
              </Typography>
              <Typography variant="body2">
                {t('setupWizard:tenant_tab.evidence_gate_flag')}:{' '}
                {evidenceGate.gate_enabled
                  ? t('setupWizard:tenant_tab.evidence_gate_on')
                  : t('setupWizard:tenant_tab.evidence_gate_off')}
              </Typography>
              {(evidenceGate.error_code || evidenceGate.diagnostic_error_code) && (
                <Typography variant="body2">
                  {t('setupWizard:tenant_tab.evidence_error')}:{' '}
                  {evidenceGate.error_code || evidenceGate.diagnostic_error_code}
                </Typography>
              )}
              {(evidenceGate.message_key || evidenceGate.diagnostic_message_key) && (
                <Typography variant="body2">
                  {(() => {
                    const key = evidenceGate.message_key || evidenceGate.diagnostic_message_key;
                    const local = String(key).startsWith('setupWizard.')
                      ? String(key).slice('setupWizard.'.length)
                      : key;
                    return t(`setupWizard:${local}`, { defaultValue: key });
                  })()}
                </Typography>
              )}
              <Typography variant="body2">
                {t('setupWizard:tenant_tab.evidence_source')}:{' '}
                {evidenceGate.source || 'mafert_app_setup_center'}
              </Typography>
              {evidenceGate.reported_at && (
                <Typography variant="body2">
                  {t('setupWizard:tenant_tab.evidence_reported_at')}: {evidenceGate.reported_at}
                </Typography>
              )}
              {evidenceGate.evidence_stale && (
                <Typography variant="body2" color="warning.main">
                  {t('setupWizard:errors.evidenceStale')}
                </Typography>
              )}
              {evidenceGate.schema_version != null && (
                <Typography variant="body2">
                  {t('setupWizard:tenant_tab.evidence_schema')}: v{evidenceGate.schema_version}
                </Typography>
              )}
              {(evidenceGate.missing_app_steps || []).length > 0 && (
                <Typography variant="body2">
                  {t('setupWizard:tenant_tab.evidence_missing_app_steps')}:{' '}
                  {evidenceGate.missing_app_steps.join(', ')}
                </Typography>
              )}
              {(evidenceGate.incomplete_app_steps || []).length > 0 && (
                <Typography variant="body2">
                  {t('setupWizard:tenant_tab.evidence_incomplete_app_steps')}:{' '}
                  {evidenceGate.incomplete_app_steps.join(', ')}
                </Typography>
              )}
              {(evidenceGate.unknown_app_steps || []).length > 0 && (
                <Typography variant="body2">
                  {t('setupWizard:tenant_tab.evidence_unknown_app_steps')}:{' '}
                  {evidenceGate.unknown_app_steps.join(', ')}
                </Typography>
              )}
              {evidenceBlocksComplete && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {t('setupWizard:tenant_tab.complete_blocked_by_evidence')}
                </Typography>
              )}
              {!evidenceGate.gate_enabled && !evidenceGate.evidence_supports_completion && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {t('setupWizard:tenant_tab.evidence_gate_off_hint')}
                </Typography>
              )}
            </Alert>
          )}

          {warnings.length > 0 && (
            <Alert severity="warning">
              {warnings.map((code) => (
                <Typography key={code} variant="body2">
                  {formatSetupWarning(code)}
                </Typography>
              ))}
            </Alert>
          )}

          {canManage && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {setupSession.status === 'not_started' && (
                <Button variant="contained" onClick={handleStartSession}>
                  {t('setupWizard:actions.start_session')}
                </Button>
              )}
              {setupSession.status !== 'completed' && (
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleCompleteSession}
                  disabled={(!canComplete && !canManage) || evidenceBlocksComplete}
                >
                  {t('setupWizard:actions.complete_session')}
                </Button>
              )}
              {setupSession.status === 'completed' && (
                <Button variant="outlined" onClick={handleReopenSession}>
                  {t('setupWizard:actions.reopen_session')}
                </Button>
              )}
            </Stack>
          )}

          <Typography variant="subtitle2">{t('setupWizard:tenant_tab.steps_title')}</Typography>
          <DataTable
            columns={stepColumns}
            rows={context.setup_steps || []}
            rowKey="id"
            emptyMessage={t('setupWizard:tenant_tab.no_steps')}
          />

          <Typography variant="subtitle2">{t('setupWizard:tenant_tab.events_title')}</Typography>
          <DataTable
            columns={eventColumns}
            rows={events}
            rowKey="id"
            emptyMessage={t('setupWizard:tenant_tab.no_events')}
          />
        </>
      )}

      <Dialog open={Boolean(dialog)} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialog?.type === 'skip'
            ? t('setupWizard:dialogs.skip_title')
            : t('setupWizard:dialogs.block_title')}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label={t('setupWizard:dialogs.reason')}
            value={dialogReason}
            onChange={(e) => setDialogReason(e.target.value)}
            sx={{ mt: 1 }}
            required={dialog?.type === 'block'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>{t('common:cancel')}</Button>
          <Button
            variant="contained"
            color={dialog?.type === 'block' ? 'warning' : 'primary'}
            onClick={dialog?.type === 'skip' ? handleSkipStep : handleBlockStep}
            disabled={dialog?.type === 'block' && !dialogReason.trim()}
          >
            {dialog?.type === 'skip' ? t('setupWizard:actions.skip_step') : t('setupWizard:actions.block_step')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
